import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  IconButton,
  Text,
  Badge,
  Center,
  Button,
  Checkbox,
  Icon,
  HStack,
} from '@chakra-ui/react';
import {
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
} from '@/components/ui/menu';
import { DataTable, dataTableButtonStyles } from '@/components/data-table';
import { type ColumnDef } from '@tanstack/react-table';
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaChevronDown,
  FaEllipsisV,
  FaRegCopy,
} from 'react-icons/fa';
import { format } from 'date-fns';
import { useLocationsData } from '../locations.hook';
import { Location } from '../locations.types';

interface LocationsListProps {
  onEdit?: (location: Location) => void;
  onDelete?: (location: Location) => void;
  onView?: (location: Location) => void;
  onCreate?: () => void;
  onSelect?: (location: Location) => void;
  selectedLocationId?: string;
  organizationId?: string;
}

export const LocationsList = ({
  onEdit,
  onDelete,
  onView,
  onSelect,
  selectedLocationId,
  organizationId,
}: LocationsListProps) => {
  const [params, setParams] = useState({
    page: 1,
    perPage: 25,
    sort: 'updated_at',
    order: 'DESC' as const,
    filter: {},
    organizationId,
  });

  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>(
    '',
  );
  const [globalFilter, setGlobalFilter] = useState('');

  const { locations, total, isLoading, isError, error, refetch } =
    useLocationsData(organizationId, params);

  // Update params when filters change
  React.useEffect(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      organizationId,
      filter: {
        ...prev.filter,
        status: statusFilter || undefined,
      },
    }));
    refetch();
  }, [statusFilter, organizationId, refetch]);

  // Helper functions
  const getStatusColor = (isActive: boolean): string => {
    return isActive ? 'blue' : 'gray'; // Changed from green to blue to match AdminPage
  };

  const getRowStyles = (location: Location) => {
    if (!location.auth_id) {
      return { backgroundColor: 'var(--chakra-colors-yellow-50)' };
    }
    return {};
  };

  const handleEdit = useCallback(
    (location: Location) => {
      onEdit?.(location);
    },
    [onEdit],
  );

  const handleDelete = useCallback(
    (location: Location) => {
      onDelete?.(location);
    },
    [onDelete],
  );

  const handleView = useCallback(
    (location: Location) => {
      onView?.(location);
    },
    [onView],
  );

  const handleSelect = useCallback(
    (location: Location) => {
      onSelect?.(location);
    },
    [onSelect],
  );

  const handleCopy = useCallback((id: string) => {
    navigator.clipboard.writeText(id);
  }, []);

  // Define columns
  const columns = useMemo<ColumnDef<Location>[]>(
    () => [
      // Selection column
      {
        id: 'select',
        header: () => (
          <Checkbox.Root checked={false} disabled={true}>
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        ),
        cell: ({ row }) => {
          const isSelected = selectedLocationId === row.original.id;
          return (
            <Checkbox.Root
              checked={isSelected}
              onCheckedChange={(details) => {
                if (details.checked && onSelect) {
                  handleSelect(row.original);
                }
              }}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
            </Checkbox.Root>
          );
        },
        enableSorting: false,
        enableHiding: false,
        enableResizing: true,
        minSize: 50,
        maxSize: 80,
      },
      // Name column
      {
        accessorKey: 'name',
        header: 'NAME',
        cell: ({ row }) => {
          const isSelected = selectedLocationId === row.original.id;
          return (
            <Box
              cursor={onSelect ? 'pointer' : 'default'}
              onClick={() => {
                if (onSelect) {
                  console.log('Clicking on location:', row.original.name);
                  handleSelect(row.original);
                }
              }}
              bg={isSelected ? 'blue.50' : 'transparent'}
              px={2}
              py={1}
              borderRadius="md"
              transition="all 0.2s"
              _hover={onSelect ? { bg: 'blue.100' } : {}}
              width="100%"
            >
              <Text
                fontWeight="semibold"
                color={isSelected ? 'blue.600' : 'gray.900'}
                _hover={onSelect ? { color: 'blue.600' } : {}}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                title={row.original.name}
              >
                {row.original.name}
              </Text>
            </Box>
          );
        },
        minSize: 120,
        enableResizing: true,
      },
      {
        accessorKey: 'id',
        header: 'ID',
        cell: ({ row }) => (
          <Text
            color="gray.700"
            fontSize="sm"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={row.original.id || '-'}
          >
            {row.original.id || '-'}
            <Badge
              onClick={() => handleCopy(row.original.id)}
              textTransform="capitalize"
              cursor="pointer"
              ml={2}
            >
              <Icon as={FaRegCopy} />
            </Badge>
          </Text>
        ),
        minSize: 80,
        enableResizing: true,
      },

      // API Key column
      {
        accessorKey: 'location_api_key',
        header: 'API KEY',
        cell: ({ row }) => (
          <Box>
            {row.original.location_api_key ? (
              <HStack gap={2}>
                <Text
                  color="gray.700"
                  fontSize="sm"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  maxW="150px"
                  title={row.original.location_api_key}
                >
                  {row.original.location_api_key}
                </Text>
                <Badge
                  onClick={() => handleCopy(row.original.location_api_key!)}
                  textTransform="capitalize"
                  cursor="pointer"
                  colorScheme="blue"
                  size="sm"
                >
                  <Icon as={FaRegCopy} />
                </Badge>
              </HStack>
            ) : (
              <Badge
                colorScheme="gray"
                variant="subtle"
                size="sm"
                textTransform="capitalize"
              >
                No API Key
              </Badge>
            )}
          </Box>
        ),
        minSize: 120,
        enableResizing: true,
      },
      // Email column

      {
        accessorKey: 'email',
        header: 'EMAIL',
        cell: ({ row }) => (
          <Text
            color="gray.700"
            fontSize="sm"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={row.original.email || '-'}
          >
            {row.original.email || '-'}
          </Text>
        ),
        minSize: 80,
        enableResizing: true,
      },
      // Address column
      {
        accessorKey: 'address',
        header: 'ADDRESS',
        cell: ({ row }) => (
          <Text
            color="gray.700"
            fontSize="sm"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={row.original.address || '-'}
          >
            {row.original.address || '-'}
          </Text>
        ),
        minSize: 100,
        enableResizing: true,
      },

      // Phone column
      {
        accessorKey: 'phone',
        header: 'PHONE',
        cell: ({ row }) => (
          <Text
            color="gray.700"
            fontSize="sm"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={row.original.phone || '-'}
          >
            {row.original.phone || '-'}
          </Text>
        ),
        minSize: 80,
        enableResizing: true,
      },
      // Status column
      {
        accessorKey: 'is_active',
        header: 'STATUS',
        cell: ({ row }) => (
          <Badge
            colorScheme={getStatusColor(row.original.is_active ?? false)}
            textTransform="capitalize"
          >
            {row.original.is_active ? 'Active' : 'Inactive'}
          </Badge>
        ),
        minSize: 80,
        enableResizing: true,
      },
      // Auth ID column
      {
        accessorKey: 'auth_id',
        header: 'AUTH ID',
        cell: ({ row }) => (
          <Text
            color="gray.700"
            fontSize="sm"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={row.original.auth_id || 'No Auth ID'}
          >
            {row.original.auth_id || (
              <Badge
                colorScheme="yellow"
                variant="subtle"
                size="sm"
                textTransform="capitalize"
              >
                No Auth ID
              </Badge>
            )}
          </Text>
        ),
        minSize: 90,
        enableResizing: true,
      },

      // Agent Delay column
      {
        accessorKey: 'agent_delay',
        header: 'DELAY',
        cell: ({ row }) => (
          <Badge
            colorScheme={row.original.agent_delay ? 'blue' : 'gray'}
            variant="subtle"
            size="sm"
            textTransform="capitalize"
          >
            {row.original.agent_delay ? 'Enabled' : 'Disabled'}
          </Badge>
        ),
        minSize: 80,
        enableResizing: true,
      },
      // Agent Delay After Employee column
      {
        accessorKey: 'agent_delay_after_employee',
        header: 'DELAY AFTER EMPLOYEE',
        cell: ({ row }) => (
          <Badge
            colorScheme={
              row.original.agent_delay_after_employee ? 'blue' : 'gray'
            }
            variant="subtle"
            size="sm"
            textTransform="capitalize"
          >
            {row.original.agent_delay_after_employee ? 'Enabled' : 'Disabled'}
          </Badge>
        ),
        minSize: 100,
        enableResizing: true,
      },
      // Agent Default Active column
      {
        accessorKey: 'agent_default_active',
        header: 'AGENT DEFAULT STATUS',
        cell: ({ row }) => (
          <Badge
            colorScheme={row.original.agent_default_active ? 'green' : 'red'}
            variant="subtle"
            size="sm"
            textTransform="capitalize"
          >
            {row.original.agent_default_active ? 'Active' : 'Inactive'}
          </Badge>
        ),
        minSize: 90,
        enableResizing: true,
      },
      // Max Interactions Per Month column
      {
        accessorKey: 'max_interaction_per_month',
        header: 'MAX INTERACTIONS PER MONTH',
        cell: ({ row }) => (
          <Text color="gray.700" fontSize="sm">
            {row.original.max_interaction_per_month || '-'}
          </Text>
        ),
        minSize: 100,
        enableResizing: true,
      },
      // Max Interactions Per Session column
      {
        accessorKey: 'max_interaction_per_session',
        header: 'MAX INTERACTIONS PER SESSION',
        cell: ({ row }) => (
          <Text color="gray.700" fontSize="sm">
            {row.original.max_interaction_per_session || '-'}
          </Text>
        ),
        minSize: 110,
        enableResizing: true,
      },
      // Pay Per Use column
      {
        accessorKey: 'pay_per_use',
        header: 'PAY PER USE',
        cell: ({ row }) => (
          <Badge
            colorScheme={row.original.pay_per_use ? 'purple' : 'gray'}
            variant="subtle"
            size="sm"
            textTransform="capitalize"
          >
            {row.original.pay_per_use ? 'Enabled' : 'Disabled'}
          </Badge>
        ),
        minSize: 90,
        enableResizing: true,
      },

      // Created At column
      {
        accessorKey: 'created_at',
        header: 'CREATED',
        cell: ({ row }) => (
          <Text
            color="gray.700"
            fontSize="sm"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={
              row.original.created_at
                ? format(new Date(row.original.created_at), 'MMM dd, yyyy')
                : '-'
            }
          >
            {row.original.created_at
              ? format(new Date(row.original.created_at), 'MMM dd, yyyy')
              : '-'}
          </Text>
        ),
        minSize: 100,
        enableResizing: true,
      },
      // Actions column
      {
        id: 'actions',
        header: 'ACTIONS',
        cell: ({ row }) => (
          <MenuRoot>
            <MenuTrigger asChild>
              <IconButton
                aria-label="More actions"
                size="sm"
                variant="ghost"
                borderRadius="xl"
                color="gray.500"
                _hover={{
                  bg: 'gray.100',
                  color: 'gray.700',
                }}
              >
                <FaEllipsisV />
              </IconButton>
            </MenuTrigger>
            <MenuContent>
              {onView && (
                <MenuItem value="view" onClick={() => handleView(row.original)}>
                  <FaEye />
                  View Location
                </MenuItem>
              )}
              {onEdit && (
                <MenuItem value="edit" onClick={() => handleEdit(row.original)}>
                  <FaEdit />
                  Edit Location
                </MenuItem>
              )}
              {onDelete && (
                <MenuItem
                  value="delete"
                  onClick={() => handleDelete(row.original)}
                >
                  <FaTrash />
                  Delete Location
                </MenuItem>
              )}
            </MenuContent>
          </MenuRoot>
        ),
        enableSorting: false,
        enableResizing: false,
        minSize: 80,
        maxSize: 80,
      },
    ],
    [
      onEdit,
      onDelete,
      onView,
      onSelect,
      selectedLocationId,
      handleDelete,
      handleEdit,
      handleSelect,
      handleView,
      handleCopy,
    ],
  );

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setParams((prev) => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (newPerPage: number) => {
    setParams((prev) => ({ ...prev, page: 1, perPage: newPerPage }));
  };

  if (isError) {
    return (
      <Center py={8}>
        <Text color="red.500">
          Error loading locations: {error?.message || 'Unknown error'}
        </Text>
      </Center>
    );
  }

  // Status filter component
  const statusFilterComponent = (
    <MenuRoot>
      <MenuTrigger asChild>
        <Button
          {...dataTableButtonStyles.outline}
          justifyContent="space-between"
          minW="140px"
        >
          Status: {statusFilter || 'All'}
          <FaChevronDown />
        </Button>
      </MenuTrigger>
      <MenuContent>
        <MenuItem value="" onClick={() => setStatusFilter('')}>
          All Statuses
        </MenuItem>
        <MenuItem value="active" onClick={() => setStatusFilter('active')}>
          Active
        </MenuItem>
        <MenuItem value="inactive" onClick={() => setStatusFilter('inactive')}>
          Inactive
        </MenuItem>
      </MenuContent>
    </MenuRoot>
  );

  return (
    <DataTable
      data={locations || []}
      columns={columns}
      isLoading={isLoading}
      total={total}
      page={params.page}
      perPage={params.perPage}
      onPageChange={handlePageChange}
      onPerPageChange={handlePerPageChange}
      searchPlaceholder="Search locations..."
      globalFilter={globalFilter}
      onGlobalFilterChange={setGlobalFilter}
      filters={statusFilterComponent}
      bulkActions={null}
      enableSelection={false}
      emptyMessage="No locations found"
      selectedRowId={selectedLocationId}
      getRowId={(location) => location.id}
      getRowStyles={getRowStyles}
    />
  );
};
